import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { User } from 'types/interfaces/user';

export const meApi = createApi({
  reducerPath: 'meApi',
  baseQuery: baseQuery,
  tagTypes: ['User'],
  endpoints: (builder) => ({
    getMe: builder.query<User, void>({
      query: () => ({
        url: '/users/me',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['User'],
    }),
    updateAvatar: builder.mutation<User, { avatarUrl: string }>({
      query: ({ avatarUrl }) => ({
        url: '/users/avatar',
        method: 'PUT',
        body: JSON.stringify({ avatarUrl }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      invalidatesTags: ['User'],
    }),
    updateAnimatedBorder: builder.mutation<{ showAnimatedBorder: boolean }, { enabled: boolean }>({
      query: ({ enabled }) => ({
        url: '/settings/animated-border',
        method: 'PUT',
        body: JSON.stringify({ enabled }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      // Optimistic update: immediately reflect in cached user data
      async onQueryStarted({ enabled }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.showAnimatedBorder = enabled;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateBorderColors: builder.mutation<{ borderColor1: string; borderColor2: string }, { borderColor1: string; borderColor2: string }>({
      query: ({ borderColor1, borderColor2 }) => ({
        url: '/settings/border-colors',
        method: 'PUT',
        body: JSON.stringify({ borderColor1, borderColor2 }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      async onQueryStarted({ borderColor1, borderColor2 }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.borderColor1 = borderColor1;
            draft.borderColor2 = borderColor2;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateBubbleColors: builder.mutation<{ bubbleColor1: string; bubbleColor2: string }, { color1: string; color2: string }>({
      query: ({ color1, color2 }) => ({
        url: '/settings/bubble-colors',
        method: 'PUT',
        body: JSON.stringify({ color1, color2 }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      async onQueryStarted({ color1, color2 }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.bubbleColor1 = color1;
            draft.bubbleColor2 = color2;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateCustomSlug: builder.mutation<{ success: boolean; customSlug: string }, { slug: string }>({
      query: ({ slug }) => ({
        url: '/settings/custom-slug',
        method: 'PUT',
        body: JSON.stringify({ slug }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      async onQueryStarted({ slug }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.customSlug = slug;
            draft.customSlugSetAt = new Date().toISOString();
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    removeCustomSlug: builder.mutation<{ success: boolean; customSlug: null }, void>({
      query: () => ({
        url: '/settings/custom-slug',
        method: 'DELETE',
        requiresAuth: true,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.customSlug = null;
            draft.customSlugSetAt = null;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetMeQuery,
  useUpdateAvatarMutation,
  useUpdateAnimatedBorderMutation,
  useUpdateBorderColorsMutation,
  useUpdateBubbleColorsMutation,
  useUpdateCustomSlugMutation,
  useRemoveCustomSlugMutation,
} = meApi;
